import React from 'react'
import { useListState } from '@mantine/hooks'
import { Link, useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import {
  IconCubePlus,
  IconEdit,
  IconExternalLink,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ActionIcon, Badge, Button, Group, List, Paper, Text, Title } from '@mantine/core'

import { ModalFormType, PageProps } from '@customTypes/page'
import { ClientBaseType, GetClientsType } from '@customTypes/client'

import DataRepo from '@api/datasource/data'
import queryClient from '@api/datasource/query'

import { UI } from '@constants/app'
import QueryKeys from '@constants/queryKeys'
import { RoutesApp } from '@constants/routes'
import { ClientTypeOptions, PriorityClient } from '@constants/client'

import { ErrorService } from '@utils/error'
import { isLoadingMutation, isLoadingOrRefetchQuery } from '@utils/network'
import { onFilterClient } from '@utils/client'

import { useParams } from '@hooks/params'
import { useIsMobile, useTableMinWidth } from '@hooks/mobile'

import ClientForm, { ClientFormDataType } from '@components/client/form'
import ConfirmationModal from '@components/shared/confirmationModal'
import TableCustom, { GenericColumnType } from '@components/shared/table'
import SettingsModal from '@components/client/settings'
import Cont from '@components/shared/cont'
import Input from '@components/shared/input'

const Client = (props: PageProps) => {
  const [modalForm, setModalForm] = React.useState<ModalFormType<ClientFormDataType>>({
    open: false,
  })

  const [settingModal, setSettingModal] = React.useState<ModalFormType>({ open: false })

  const [clientType, setClientType] = React.useState<'prospect' | 'client' | null>('client')

  const navigate = useNavigate()

  const isMobile = useIsMobile()

  const minWidth = useTableMinWidth(isMobile)

  const { cardPreview } = props

  const [modalDelete, setModalDelete] = React.useState<ClientBaseType[] | null>(null)

  const [params, setParams] = useParams<GetClientsType>({
    queryKey: QueryKeys.GET_CLIENTS_KEY,
    initialParams: {},
  })

  const [selectedRows, handlers] = useListState<string>([])

  const clientsQuery = useQuery({
    queryKey: [QueryKeys.GET_CLIENTS_KEY],
    queryFn: async () => {
      const response = await DataRepo.clientEntityService.getClients({})

      return response
    },
    select: (data) => {
      let countClients = 0
      let countProspects = 0

      for (const item of data) {
        if (item.type === 'client') {
          countClients += 1
        } else if (item.type === 'prospect') {
          countProspects += 1
        }
      }
      return {
        items: data,
        client: countClients,
        prospect: countProspects,
      }
    },
  })

  const clientsDeleteMutation = useMutation<void, ErrorService, string[]>({
    mutationFn: async (uids) => {
      const response = await DataRepo.clientEntityService.deleteClients(uids)

      setParams({})

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_CLIENTS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al eliminar el cliente',
        })
      }

      notifications.show({ color: 'green', title: 'Éxito', message: 'Eliminación exitosa' })

      setModalDelete(null)
      handlers.setState([])
    },
  })

  const memoFilterClient = React.useMemo(() => {
    const results = onFilterClient({
      data: clientsQuery.data?.items || [],
      params,
      limit: cardPreview ? 5 : undefined,
    })

    return results.filter((item) => {
      if (!clientType || cardPreview) return true
      return item.type === clientType
    })
  }, [cardPreview, clientsQuery.data?.items, params, clientType])

  React.useEffect(() => {
    return () => {
      handlers.setState([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoading = isLoadingOrRefetchQuery(clientsQuery)

  if (cardPreview) {
    return (
      <React.Fragment>
        <Paper
          withBorder
          className="cd-flex cd-flex-col cd-gap-y-[1rem]"
          p="lg"
          radius="md"
          shadow="md"
        >
          <Link
            className="hover:cd-text-blue-400 cd-cursor-pointer cd-w-fit cd-flex cd-flex-row cd-gap-x-[0.5rem] cd-items-center"
            to={RoutesApp.CLIENTS}
          >
            <Title order={3}>Clientes</Title>
            <IconExternalLink size={20} />
          </Link>
          <TableCustom<ClientBaseType>
            columns={buildColumns()}
            data={memoFilterClient}
            hidePagination={cardPreview}
            keyId="uid"
            loading={isLoading}
            loadingMessage="Cargando clientes"
            minWidth={minWidth}
            onDetail={(item) => {
              navigate(RoutesApp.CLIENT.replace(':uid', item.uid))
            }}
          />
        </Paper>
      </React.Fragment>
    )
  }

  return (
    <Cont>
      <div className="cd-flex cd-flex-col cd-gap-y-[2rem]">
        <div className="cd-flex cd-flex-col md:cd-flex-row cd-gap-[1rem] cd-grow">
          <Text className="cd-text-lg cd-grow">Clientes</Text>
          <Button
            fullWidth
            className="md:cd-basis-[15%]"
            color="gray"
            leftSection={<IconSettings size={18} />}
            size={UI.Size}
            variant="filled"
            onClick={() => setSettingModal({ open: true })}
          >
            Configuración
          </Button>
          <Button
            fullWidth
            className="md:cd-basis-[20%]"
            color="green"
            leftSection={<IconCubePlus size={18} />}
            size={UI.Size}
            variant="filled"
            onClick={() => setModalForm({ open: true })}
          >
            Agregar
          </Button>
        </div>

        <Input
          data={Object.values(ClientTypeOptions).map((item) => ({
            label: `${item.label} (${clientsQuery.data?.[item.value as keyof typeof clientsQuery.data] || 0})`,
            value: item.value,
          }))}
          typeInput="segmentedControl"
          value={clientType || ''}
          onChange={(value) => {
            setClientType(value as 'prospect' | 'client' | null)
          }}
        />

        <TableCustom<ClientBaseType>
          allData={clientsQuery.data?.items}
          columns={buildColumns()}
          data={memoFilterClient}
          defaultColumnSearch="name"
          keyId="uid"
          loading={isLoading}
          loadingMessage="Cargando clientes"
          minWidth={minWidth}
          selectedRows={selectedRows}
          onChangeSelected={handlers}
          onDelete={(ids) => {
            const data = clientsQuery.data?.items?.filter((item) => ids.includes(item.uid))

            if (!data?.length) return

            setModalDelete(data)
          }}
          onDetail={(item) => {
            navigate(RoutesApp.CLIENT.replace(':uid', item.uid))
          }}
          onSearch={(query, field) => setParams({ query, field })}
        />
      </div>

      <ClientForm
        modal
        data={modalForm.data}
        open={modalForm.open}
        size="70%"
        onClose={() => setModalForm({ open: false })}
        onSubmitted={() => {
          setParams({})
        }}
      />

      <SettingsModal
        modal
        open={settingModal.open}
        size="xl"
        onClose={() => setSettingModal({ open: false })}
      />

      <ConfirmationModal
        cancelColor="gray"
        cancelText="Cancelar"
        confirmColor="red"
        confirmText="Eliminar"
        loading={isLoadingMutation(clientsDeleteMutation)}
        open={Boolean(modalDelete?.length)}
        title="Eliminar cliente"
        onCancel={() => setModalDelete(null)}
        onConfirm={() => {
          if (!modalDelete) return

          clientsDeleteMutation.mutate(modalDelete.map((item) => item.uid))
        }}
      >
        {modalDelete?.length === 1 && (
          <Text className="cd-text-base">
            ¿Estás seguro que deseas eliminar la cliente{' '}
            <Text inherit className="cd-text-base cd-font-bold" component="span">
              {modalDelete[0].name}
            </Text>
            ?
          </Text>
        )}
        {modalDelete && modalDelete?.length > 1 && (
          <React.Fragment>
            <Text className="cd-text-base">
              ¿Estás seguro que deseas eliminar los siguientes clientes?
            </Text>
            <List withPadding className="cd-mt-[0.5rem] cd-list-disc" type="unordered">
              {modalDelete.map((item) => (
                <List.Item key={item.uid}>
                  <Text className="cd-text-base">{item.name}</Text>
                </List.Item>
              ))}
            </List>
          </React.Fragment>
        )}
      </ConfirmationModal>
    </Cont>
  )

  function buildColumns() {
    return [
      {
        key: 'ruc',
        label: 'RUC',
        width: '12%',
        type: 'text',
        searchable: true,
        defaultOnClick: true,
      },
      {
        key: 'status',
        label: 'Estado',
        width: '13%',
        type: 'calc',
        searchable: true,
        defaultOnClick: true,
        align: 'center',
        render: ({ status }) => (
          <Badge color={status?.color || 'gray'} size="md">
            {status?.name || 'Sin estado'}
          </Badge>
        ),
      },
      {
        key: 'type',
        label: 'Tipo',
        width: '13%',
        type: 'calc',
        align: 'center',
        defaultOnClick: true,
        render: ({ type }) => (
          <Badge color={ClientTypeOptions[type]?.color || 'gray'} size="md">
            {ClientTypeOptions[type]?.label || 'Sin tipo'}
          </Badge>
        ),
      },
      { key: 'name', label: 'Nombre', width: '30%', type: 'text', searchable: true },
      { key: 'email', label: 'Email', width: '25%', type: 'text', searchable: true },
      {
        key: 'priority',
        label: 'Prioridad',
        width: '10%',
        type: 'calc',
        searchable: true,
        defaultOnClick: true,
        render: ({ priority }) => (
          <div className="cd-text-center">
            <Badge color={PriorityClient[priority]?.color || 'gray'}>
              {PriorityClient[priority]?.label || 'Sin prioridad'}
            </Badge>
          </div>
        ),
      },
      {
        key: 'actions',
        label: 'Acciones',
        width: '15%',
        type: 'calc',
        align: 'center',
        render: (item) => (
          <Group justify="center">
            <ActionIcon
              variant="transparent"
              onClick={() => {
                setModalForm({ open: true, data: { client: item } })
              }}
            >
              <IconEdit size={18} />
            </ActionIcon>
            <ActionIcon
              color="red"
              variant="transparent"
              onClick={() => {
                setModalDelete([item])
              }}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Group>
        ),
      },
    ] as GenericColumnType<ClientBaseType>[]
  }
}

export default Client
